import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Type } from 'lucide-react';
import { Button } from '@/components/ui/button';

const MenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  const colors = [
    { name: 'Negro', value: '#000000' },
    { name: 'Rojo', value: '#9C0F17' },
    { name: 'Azul', value: '#0066B3' },
    { name: 'Verde', value: '#28a745' },
    { name: 'Amarillo', value: '#ffc107' },
    { name: 'Gris', value: '#6c757d' },
  ];

  return (
    <div className="border-b p-2 flex items-center gap-1 flex-wrap bg-gray-50">
      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleBold().run()}
        variant={editor.isActive('bold') ? 'default' : 'ghost'}
        size="sm"
        className="h-9 w-9 p-0"
        title="Negrita (Ctrl+B)"
      >
        <Bold className="h-4 w-4" />
      </Button>

      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        variant={editor.isActive('italic') ? 'default' : 'ghost'}
        size="sm"
        className="h-9 w-9 p-0"
        title="Cursiva (Ctrl+I)"
      >
        <Italic className="h-4 w-4" />
      </Button>

      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        variant={editor.isActive('underline') ? 'default' : 'ghost'}
        size="sm"
        className="h-9 w-9 p-0"
        title="Subrayado (Ctrl+U)"
      >
        <UnderlineIcon className="h-4 w-4" />
      </Button>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Color picker */}
      <div className="flex items-center gap-1">
        <span className="text-xs text-gray-600 mr-1">Color:</span>
        {colors.map((color) => (
          <button
            key={color.value}
            type="button"
            onClick={() => editor.chain().focus().setColor(color.value).run()}
            className={`w-6 h-6 rounded border-2 hover:scale-110 transition-transform ${
              editor.isActive('textStyle', { color: color.value }) ? 'border-gray-900' : 'border-gray-300'
            }`}
            style={{ backgroundColor: color.value }}
            title={color.name}
          />
        ))}
      </div>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      {/* Heading sizes */}
      <select
        onChange={(e) => {
          const level = parseInt(e.target.value);
          if (level === 0) {
            editor.chain().focus().setParagraph().run();
          } else {
            editor.chain().focus().toggleHeading({ level }).run();
          }
          e.target.value = '';
        }}
        className="h-9 text-sm border rounded px-2 bg-white"
        value=""
      >
        <option value="" disabled>Tamaño</option>
        <option value="0">Normal</option>
        <option value="3">Grande</option>
        <option value="2">Muy Grande</option>
      </select>

      <div className="w-px h-6 bg-gray-300 mx-1" />

      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        variant={editor.isActive('bulletList') ? 'default' : 'ghost'}
        size="sm"
        className="h-9 w-9 p-0"
        title="Lista con viñetas"
      >
        <List className="h-4 w-4" />
      </Button>

      <Button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        variant={editor.isActive('orderedList') ? 'default' : 'ghost'}
        size="sm"
        className="h-9 w-9 p-0"
        title="Lista numerada"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
    </div>
  );
};

const RichTextEditor = ({ value, onChange, placeholder, minHeight = '120px' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none p-3',
        style: `min-height: ${minHeight}`,
      },
    },
  });

  // Update editor content when value changes externally
  React.useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  return (
    <div className="border rounded-md overflow-hidden bg-white">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} />
      <style jsx global>{`
        .ProseMirror {
          min-height: ${minHeight};
        }
        .ProseMirror:focus {
          outline: none;
        }
        .ProseMirror p {
          margin: 0.5em 0;
        }
        .ProseMirror h1 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 0.5em 0;
        }
        .ProseMirror h2 {
          font-size: 1.3em;
          font-weight: bold;
          margin: 0.5em 0;
        }
        .ProseMirror h3 {
          font-size: 1.1em;
          font-weight: bold;
          margin: 0.5em 0;
        }
        .ProseMirror ul,
        .ProseMirror ol {
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .ProseMirror li {
          margin: 0.25em 0;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
